import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Autocomplete } from "@mui/material";
import AnswersDialog from "../shared/AnswersDialog";
import SimpleConfigTable from "../shared/SimpleConfigTable";
import { questionnaireAnswersApi, questionnairesApi } from "../../../../services/FeedbackServices";
import TablePagerBar from "../../TicketManagement/shared/TablePagerBar";

export default function AnswersPanel() {
  const [rows, setRows] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionId, setQuestionId] = useState(null);
  const [open, setOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  const fetchQuestions = () => {
    questionnairesApi("get")
      .then((res) => {
        const list = res?.data || [];
        setQuestions(list);
      })
      .catch(() => {
        setQuestions([]);
      });
  };

  const fetchRows = (qid) => {
    const params = qid ? { questionnaire_id: qid } : null;
    questionnaireAnswersApi("get", null, params)
      .then((res) => {
        setRows(res?.data || []);
      })
      .catch(() => {
        setRows([]);
      });
  };

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRows(questionId);
  }, [questionId]);

  useEffect(() => {
    setPagination({ page: 1, perPage: 10 });
  }, [questionId, rows?.length]);

  const columns = useMemo(
    () => [
      { key: "question", label: "Question" },
      { key: "code", label: "Code" },
      { key: "answer_type", label: "Answer Type" },
      { key: "is_deleted", label: "Deleted" },
    ],
    []
  );

  const displayRows = useMemo(() => {
    return (rows || []).map((r) => ({
      ...r,
      question: r?.questionnaire?.question || r?.questionnaire?.name || "-",
      is_deleted: r?.is_deleted ? "Yes" : "No",
    }));
  }, [rows]);

  const pagedRows = useMemo(() => {
    const page = pagination.page || 1;
    const perPage = pagination.perPage || 10;
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return (displayRows || []).slice(start, end);
  }, [displayRows, pagination.page, pagination.perPage]);

  const handleAdd = () => {
    setEditRow(null);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditRow(null);
  };

  const handleSubmit = (payload) => {
    const id = payload?.id;
    if (id) {
      const patchPayload = {
        code: payload?.code,
        name: payload?.name,
        description: payload?.description,
        questionnaire_id: payload?.questionnaire_id,
        answer_type: payload?.answer_type,
        answer_details: payload?.answer_details || [],
      };
      questionnaireAnswersApi("patch", patchPayload, null, id)
        .then(() => {
          fetchRows(questionId);
          handleClose();
        })
        .catch(() => {});
      return;
    }

    const postPayload = {
      code: payload?.code,
      name: payload?.name,
      description: payload?.description,
      questionnaire_id: payload?.questionnaire_id || questionId,
      answer_type: payload?.answer_type,
      answer_details: payload?.answer_details || [],
    };
    questionnaireAnswersApi("post", postPayload)
      .then(() => {
        fetchRows(questionId);
        handleClose();
      })
      .catch(() => {});
  };

  const handleInactive = (id) => {
    questionnaireAnswersApi("delete", null, null, id)
      .then(() => fetchRows(questionId))
      .catch(() => {});
  };

  return (
    <Box>
      <Box sx={{ mb: 2, maxWidth: 520 }}>
        <Autocomplete
          options={questions}
          value={questions.find((q) => String(q.id) === String(questionId)) || null}
          size={"small"}
          onChange={(e, next) => setQuestionId(next?.id || null)}
          getOptionLabel={(o) => o?.question || o?.name || ""}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          renderInput={(params) => <TextField {...params} label={"Select Question"} />}
          clearOnEscape
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant={"contained"} onClick={handleAdd} className={"text-capitalize"}>
          + Add Answer Set
        </Button>
      </Box>

      <SimpleConfigTable columns={columns} rows={pagedRows} onEdit={handleEdit} onLogicalDelete={handleInactive} />

      <TablePagerBar
        perPage={pagination.perPage}
        onPerPageChange={(e) => setPagination((p) => ({ ...p, perPage: Number(e.target.value), page: 1 }))}
        total={displayRows?.length || 0}
        page={pagination.page}
        onPageChange={(_, page) => setPagination((p) => ({ ...p, page }))}
      />

      {open && (
        <AnswersDialog
          open={open}
          row={editRow}
          questions={questions}
          defaultQuestionId={questionId}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </Box>
  );
}
